import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AccountPayable } from "@meupedaco/shared";
import { addMonths, addYears, format, isValid, parse } from "date-fns";
import { CrudPage } from "../../components/CrudPage";
import { DateInputMasked } from "../../components/DateInputMasked";
import { PaymentModal } from "../../components/PaymentModal";
import { ReversePaymentModal } from "../../components/ReversePaymentModal";
import { useAuth } from "../../auth/AuthContext";
import {
  getMonthRange,
  getYearRange,
  formatDateBR as formatDateBRInput,
  formatDateInput,
} from "../../utils/dateRange";
import { formatDateBR, formatMoneyBR } from "../../utils/format";
import { parseMoneyBR } from "../../utils/parseMoneyBr";
import { OPERATION_NATURE_PT, PAYABLE_STATUS_PT } from "../../constants/enumsPt";

type ValidationErrors = {
  from: string | null;
  to: string | null;
  range: string | null;
};

type PeriodMode = "MONTH" | "YEAR";

type PeriodState = {
  mode: PeriodMode;
  from: string;
  to: string;
};

const parseDateInput = (value: string) => {
  const trimmed = value.trim();
  const pattern = /^\d{2}\/\d{2}\/\d{4}$/;
  if (pattern.test(trimmed)) {
    const parsed = parse(trimmed, "dd/MM/yyyy", new Date());
    if (!isValid(parsed)) {
      return null;
    }
    return format(parsed, "dd/MM/yyyy") === trimmed ? parsed : null;
  }
  const isoPattern = /^\d{4}-\d{2}-\d{2}$/;
  if (isoPattern.test(trimmed)) {
    const parsed = parse(trimmed, "yyyy-MM-dd", new Date());
    if (!isValid(parsed)) {
      return null;
    }
    return format(parsed, "yyyy-MM-dd") === trimmed ? parsed : null;
  }
  const parsed = new Date(trimmed);
  if (!parsed || Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed;
};

const isValidRange = (from: Date | null, to: Date | null) =>
  Boolean(from && to && from.getTime() <= to.getTime());

const isCompleteDate = (value: string) => value.trim().length === 10;

export const PayablesPage: React.FC = () => {
  const { client } = useAuth();
  const [paymentModal, setPaymentModal] = useState<{
    show: boolean;
    itemId: number;
    itemDescription: string;
    itemAmount: number;
    itemDueDate: string;
  }>({
    show: false,
    itemId: 0,
    itemDescription: "",
    itemAmount: 0,
    itemDueDate: "",
  });
  const [reverseModal, setReverseModal] = useState<{
    show: boolean;
    itemId: number;
    itemDescription: string;
  }>({
    show: false,
    itemId: 0,
    itemDescription: "",
  });
  const [refreshList, setRefreshList] = useState<null | (() => Promise<void>)>(
    null
  );
  const currentRange = useMemo(() => getMonthRange(new Date()), []);
  const [statusFilter, setStatusFilter] = useState("OPEN");
  const [period, setPeriod] = useState<PeriodState>({
    mode: "MONTH",
    from: formatDateInput(currentRange.from),
    to: formatDateInput(currentRange.to),
  });
  const [fromInput, setFromInput] = useState(formatDateBRInput(currentRange.from));
  const [toInput, setToInput] = useState(formatDateBRInput(currentRange.to));
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({
    from: null,
    to: null,
    range: null,
  });
  const [isDateRangeValid, setIsDateRangeValid] = useState(true);
  const [payableFormMetadata, setPayableFormMetadata] = useState<{
    defaultPaymentBankAccountId: number | null;
    defaultCategoryId: number | null;
  }>({
    defaultPaymentBankAccountId: null,
    defaultCategoryId: null,
  });
  const [supplierDefaultAccountMap, setSupplierDefaultAccountMap] = useState<
    Record<number, { id: number | null; label: string | null }>
  >({});

  const listFilters = useMemo(
    () => ({
      ...(statusFilter !== "ALL" ? { status: statusFilter } : {}),
      ...(period.from ? { from: period.from } : {}),
      ...(period.to ? { to: period.to } : {}),
    }),
    [period.from, period.to, statusFilter]
  );

  const isIncomplete = (item: AccountPayable) =>
    !item.accountId ||
    !item.costCenterId ||
    !item.categoryId ||
    !item.bankAccountId ||
    !item.supplierId;

  const fetchSuppliers = useCallback(
    async (query: string) => {
      const response = await client.get(
        `/modules/suppliers?q=${encodeURIComponent(query)}&take=10&skip=0`
      );
      if (!response.ok) {
        return [];
      }
      const payload = (await response.json()) as {
        data: Array<{
          id: number;
          entity: { name: string };
          defaultAccountId?: number | null;
          defaultAccount?: { id: number; code: string; name: string } | null;
        }>;
      };
      setSupplierDefaultAccountMap((prev) => {
        const next = { ...prev };
        for (const supplier of payload.data) {
          next[supplier.id] = {
            id: supplier.defaultAccountId ?? null,
            label: supplier.defaultAccount
              ? `${supplier.defaultAccount.code} - ${supplier.defaultAccount.name}`
              : null,
          };
        }
        return next;
      });
      return payload.data.map((item) => ({
        id: item.id,
        label: item.entity.name,
      }));
    },
    [client]
  );

  const fetchCategoryOptions = useCallback(async (query: string) => {
    void query;
    const response = await client.get(`/modules/categories?take=200&skip=0`);
    if (!response.ok) {
      return [];
    }
    const payload = (await response.json()) as {
      data: Array<{ id: number; name: string }>;
    };
    return payload.data.map((item) => ({ id: item.id, label: item.name }));
  }, [client]);

  const fetchCostCenterOptions = useCallback(async (query: string) => {
    void query;
    const response = await client.get(`/modules/cost-centers?take=200&skip=0`);
    if (!response.ok) {
      return [];
    }
    const payload = (await response.json()) as {
      data: Array<{ id: number; code?: string | null; name: string }>;
    };
    return payload.data.map((item) => ({
      id: item.id,
      label: `${item.code ?? ""} ${item.name}`.trim(),
    }));
  }, [client]);

  const fetchBankAccountOptions = useCallback(async (query: string) => {
    void query;
    const response = await client.get(`/modules/bank-accounts?take=200&skip=0`);
    if (!response.ok) {
      return [];
    }
    const payload = (await response.json()) as {
      data: Array<{ id: number; bank: { name: string }; agency: string; accountNumber: string }>;
    };
    return payload.data.map((item) => ({
      id: item.id,
      label: `${item.bank.name} ${item.agency}/${item.accountNumber}`,
    }));
  }, [client]);

  const fetchAccounts = useCallback(
    async (query: string) => {
      const response = await client.get(
        `/modules/accounts?q=${encodeURIComponent(query)}&take=10&skip=0`
      );
      if (!response.ok) {
        return [];
      }
      const payload = (await response.json()) as {
        data: Array<{ id: number; code: string; name: string }>;
      };
      return payload.data.map((item) => ({
        id: item.id,
        label: `${item.code} - ${item.name}`,
      }));
    },
    [client]
  );

  const loadPayableFormMetadata = useCallback(async () => {
    const response = await client.get("/modules/payables/form-metadata");
    if (!response.ok) {
      return;
    }
    const payload = (await response.json()) as {
      data?: {
        defaultPaymentBankAccountId?: number | null;
        defaultCategoryId?: number | null;
      };
    };
    setPayableFormMetadata({
      defaultPaymentBankAccountId: payload.data?.defaultPaymentBankAccountId ?? null,
      defaultCategoryId: payload.data?.defaultCategoryId ?? null,
    });
  }, [client]);

  const navigatePeriod = useCallback((delta: number) => {
    setPeriod((current) => {
      const parsedFrom = parseDateInput(current.from) ?? new Date();
      const baseDate = new Date(parsedFrom.getFullYear(), parsedFrom.getMonth(), 1);
      const targetDate =
        current.mode === "MONTH"
          ? addMonths(baseDate, delta)
          : addYears(baseDate, delta);
      const targetRange =
        current.mode === "MONTH" ? getMonthRange(targetDate) : getYearRange(targetDate);
      const next: PeriodState = {
        mode: current.mode,
        from: formatDateInput(targetRange.from),
        to: formatDateInput(targetRange.to),
      };
      if (next.from === current.from && next.to === current.to) {
        return current;
      }
      return next;
    });
  }, []);

  const setPeriodMode = useCallback((mode: PeriodMode) => {
    setPeriod((current) => {
      if (current.mode === mode) {
        return current;
      }
      const parsedFrom = parseDateInput(current.from) ?? new Date();
      const baseDate = new Date(parsedFrom.getFullYear(), parsedFrom.getMonth(), 1);
      const range = mode === "MONTH" ? getMonthRange(baseDate) : getYearRange(baseDate);
      const next: PeriodState = {
        mode,
        from: formatDateInput(range.from),
        to: formatDateInput(range.to),
      };
      return next;
    });
  }, []);

  const PayableFormExtras: React.FC<{
    values: Record<string, string | number | boolean | null>;
    setValues: React.Dispatch<
      React.SetStateAction<Record<string, string | number | boolean | null>>
    >;
    setAutocompleteLabel: (fieldName: string, label: string) => void;
    editingId: number | null;
  }> = ({ values, setValues, setAutocompleteLabel, editingId }) => {
    const supplierDefaultsLoadedRef = useRef<Record<number, boolean>>({});
    const previousSupplierIdRef = useRef<number | null>(null);
    const didUserChangeChartAccountRef = useRef(false);
    const autoFillingChartAccountRef = useRef(false);

    useEffect(() => {
      if (!editingId) {
        return;
      }
      const dueDate =
        typeof values.dueDate === "string" ? parseDateInput(values.dueDate) : null;
      const normalized = dueDate ? formatDateInput(dueDate) : null;
      if (normalized && normalized !== values.dueDate) {
        setValues((prev) => ({ ...prev, dueDate: normalized }));
      }
    }, [editingId, setValues, values.dueDate]);

    useEffect(() => {
      if (editingId) {
        return;
      }
      const accountId = values.accountId ? Number(values.accountId) : null;
      if (!accountId) {
        return;
      }
      if (autoFillingChartAccountRef.current) {
        autoFillingChartAccountRef.current = false;
        return;
      }
      didUserChangeChartAccountRef.current = true;
    }, [editingId, values.accountId]);

    useEffect(() => {
      if (editingId) {
        return;
      }
      void loadPayableFormMetadata();
    }, [editingId]);

    useEffect(() => {
      if (editingId || values.bankAccountId || !payableFormMetadata.defaultPaymentBankAccountId) {
        return;
      }
      setValues((prev) => ({
        ...prev,
        bankAccountId: payableFormMetadata.defaultPaymentBankAccountId,
      }));
    }, [editingId, payableFormMetadata.defaultPaymentBankAccountId, setValues, values.bankAccountId]);

    useEffect(() => {
      if (editingId || values.categoryId || !payableFormMetadata.defaultCategoryId) {
        return;
      }
      setValues((prev) => ({
        ...prev,
        categoryId: payableFormMetadata.defaultCategoryId,
      }));
    }, [editingId, payableFormMetadata.defaultCategoryId, setValues, values.categoryId]);

    useEffect(() => {
      if (editingId || values.accountId) {
        return;
      }
      const supplierId = values.supplierId ? Number(values.supplierId) : null;
      if (!supplierId) {
        previousSupplierIdRef.current = null;
        return;
      }
      const supplierChanged = previousSupplierIdRef.current !== supplierId;
      previousSupplierIdRef.current = supplierId;
      if (didUserChangeChartAccountRef.current) {
        return;
      }

      const applyDefaultAccount = (defaultAccountId: number, label?: string | null) => {
        autoFillingChartAccountRef.current = true;
        setValues((prev) => ({
          ...prev,
          accountId: defaultAccountId,
        }));
        if (label) {
          setAutocompleteLabel("accountId", label);
        }
      };

      const mappedDefault = supplierDefaultAccountMap[supplierId];
      if (mappedDefault?.id && (supplierChanged || !values.accountId)) {
        applyDefaultAccount(mappedDefault.id, mappedDefault.label);
        return;
      }

      if (supplierDefaultsLoadedRef.current[supplierId]) {
        return;
      }
      supplierDefaultsLoadedRef.current[supplierId] = true;

      const loadSupplierDefaults = async () => {
        const response = await client.get(`/modules/suppliers/${supplierId}/defaults`);
        if (!response.ok) {
          return;
        }
        const payload = (await response.json()) as {
          data?: {
            defaultAccountId?: number | null;
            defaultChartAccountId?: number | null;
          };
        };
        const defaultAccountId =
          payload.data?.defaultChartAccountId ??
          payload.data?.defaultAccountId ??
          null;
        if (!defaultAccountId) {
          return;
        }
        let accountLabel: string | null = null;
        const accountResponse = await client.get(`/modules/accounts/${defaultAccountId}`);
        if (accountResponse.ok) {
          const accountPayload = (await accountResponse.json()) as {
            data?: { code?: string; name?: string };
          };
          if (accountPayload.data?.code && accountPayload.data?.name) {
            accountLabel = `${accountPayload.data.code} - ${accountPayload.data.name}`;
          }
        }
        setSupplierDefaultAccountMap((prev) => ({
          ...prev,
          [supplierId]: { id: defaultAccountId, label: accountLabel },
        }));
        applyDefaultAccount(defaultAccountId, accountLabel);
      };
      void loadSupplierDefaults();
    }, [
      client,
      editingId,
      setAutocompleteLabel,
      setValues,
      supplierDefaultAccountMap,
      values.accountId,
      values.supplierId,
    ]);

    return null;
  };

  useEffect(() => {
    const fromParsed = parseDateInput(period.from);
    const toParsed = parseDateInput(period.to);
    if (!fromParsed || !toParsed) {
      return;
    }
    setFromInput(formatDateBRInput(fromParsed));
    setToInput(formatDateBRInput(toParsed));
    setValidationErrors({ from: null, to: null, range: null });
    setIsDateRangeValid(true);
  }, [period.from, period.to]);

  useEffect(() => {
    const fromParsed = parseDateInput(fromInput);
    const toParsed = parseDateInput(toInput);
    const fromComplete = isCompleteDate(fromInput);
    const toComplete = isCompleteDate(toInput);

    const errors: ValidationErrors = {
      from: fromComplete && !fromParsed ? "Data invalida" : null,
      to: toComplete && !toParsed ? "Data invalida" : null,
      range: null,
    };

    if (fromComplete && toComplete && fromParsed && toParsed && fromParsed.getTime() > toParsed.getTime()) {
      errors.range = "A data inicial deve ser menor ou igual a data final";
    }

    const valid = fromComplete && toComplete && isValidRange(fromParsed, toParsed);
    setValidationErrors(errors);
    setIsDateRangeValid(valid && !errors.range);

    if (valid && !errors.range) {
      const nextFrom = formatDateInput(fromParsed as Date);
      const nextTo = formatDateInput(toParsed as Date);
      setPeriod((current) => {
        if (current.from === nextFrom && current.to === nextTo) {
          return current;
        }
        return {
          ...current,
          from: nextFrom,
          to: nextTo,
        };
      });
    }
  }, [fromInput, toInput]);

  type PayableDetails = AccountPayable & {
    paymentDate?: string | null;
    paidAmount?: number | null;
    interestAmount?: number | null;
    fineAmount?: number | null;
    discountAmount?: number | null;
    paymentDescription?: string | null;
    supplier?: { entity?: { name: string } | null } | null;
    category?: { name: string } | null;
    costCenter?: { name: string; code?: string | null } | null;
    bankAccount?: { bank?: { name: string } | null; agency: string; accountNumber: string } | null;
    account?: { code: string; name: string } | null;
    paymentMethod?: { name: string } | null;
    payments?: Array<{
      id: number;
      paymentDate: string;
      amount: number;
      totalAmount?: number | null;
      interestAmount?: number | null;
      fineAmount?: number | null;
      discountAmount?: number | null;
      description?: string | null;
      paymentMethod?: { name: string } | null;
      fiscalDocument?: {
        docType: string;
        docNumber?: string | null;
        docKey?: string | null;
      } | null;
    }>;
    movements?: Array<{ occurredAt: string; description?: string | null }>;
  };

  const statusLabel = (status?: string, reversed?: boolean) => {
    if (reversed) {
      return "ESTORNADO";
    }
    if (!status) {
      return "-";
    }
    if (status === "OPEN") return "ABERTO";
    if (status === "PAID") return "PAGO";
    if (status === "PARTIAL") return "PARCIAL";
    if (status === "CANCELED") return "CANCELADO";
    return status;
  };

  const formatMoney = (value?: number | null) =>
    value === null || value === undefined ? "-" : formatMoneyBR(value);

  const formatDateValue = (value?: string | null) =>
    value ? formatDateBR(value) : "-";

  return (
    <>
      <CrudPage<AccountPayable>
        title="Contas a pagar"
        endpoint="/modules/payables"
        searchPlaceholder="Buscar por descricao ou fornecedor..."
        listFilters={listFilters}
        pauseQuery={!isDateRangeValid}
        disableInteractions={false}
        lockInteractionsWhileLoading
        renderFilters={({ disabled, loading }) => (
          <div
            className="px-3 py-2 mb-2 rounded"
            style={{ background: "#f8f9fa", borderTop: "3px solid #0d6efd" }}
          >
            <small className="text-muted fw-semibold d-block mb-2">Filtros</small>
            <div className="row g-2 align-items-end">
            <div className="col-12 col-md-3">
              <label className="form-label mb-1 small fw-semibold">Status</label>
              <select
                className="form-select form-select-sm"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                disabled={disabled}
              >
                <option value="ALL">Todos</option>
                <option value="OPEN">Em aberto</option>
                <option value="PAID">Pago</option>
              </select>
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label mb-1 small fw-semibold">De</label>
              <DateInputMasked
                value={fromInput}
                onChange={setFromInput}
                invalid={Boolean(validationErrors.from)}
                disabled={disabled}
              />
              {validationErrors.from && (
                <div className="invalid-feedback">{validationErrors.from}</div>
              )}
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label mb-1 small fw-semibold">Até</label>
              <DateInputMasked
                value={toInput}
                onChange={setToInput}
                invalid={Boolean(validationErrors.to || validationErrors.range)}
                disabled={disabled}
              />
              {(validationErrors.to || validationErrors.range) && (
                <div className="invalid-feedback">
                  {validationErrors.to ?? validationErrors.range}
                </div>
              )}
            </div>
            <div className="col-12 col-md-3 d-flex flex-wrap gap-2 align-items-end">
              <div className="btn-group" role="group" aria-label="Navegacao de periodo">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  title={period.mode === "MONTH" ? "Mes anterior" : "Ano anterior"}
                  onClick={() => navigatePeriod(-1)}
                  disabled={disabled || loading}
                >
                  <i className="bi bi-chevron-left me-1" />
                  Anterior
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  title={period.mode === "MONTH" ? "Proximo mes" : "Proximo ano"}
                  onClick={() => navigatePeriod(1)}
                  disabled={disabled || loading}
                >
                  Proximo
                  <i className="bi bi-chevron-right ms-1" />
                </button>
              </div>
              <div className="btn-group" role="group" aria-label="Modo de periodo">
                <button
                  type="button"
                  className={`btn btn-sm ${
                    period.mode === "MONTH" ? "btn-primary" : "btn-outline-primary"
                  }`}
                  onClick={() => setPeriodMode("MONTH")}
                  disabled={disabled || loading}
                >
                  Mes
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${
                    period.mode === "YEAR" ? "btn-primary" : "btn-outline-primary"
                  }`}
                  onClick={() => setPeriodMode("YEAR")}
                  disabled={disabled || loading}
                >
                  Ano
                </button>
              </div>
            </div>
            {loading && (
              <div className="col-12">
                <div className="alert alert-info mb-0 py-2 small">Carregando contas a pagar...</div>
              </div>
            )}
            {!loading &&
              (!isCompleteDate(fromInput) || !isCompleteDate(toInput)) &&
              !validationErrors.from &&
              !validationErrors.to && (
                <div className="col-12">
                  <div className="alert alert-warning mb-0 py-2 small">
                    Preencha De e Até com datas completas para atualizar a busca.
                  </div>
                </div>
              )}
          </div>
          </div>
        )}
        columns={[
          {
            label: "Descrição / Fornecedor",
            render: (item) => {
              const incomplete = isIncomplete(item);
              const payable = item as PayableDetails;
              return (
                <div>
                  <div className="d-flex align-items-center gap-1">
                    {incomplete && (
                      <span
                        className="badge text-bg-warning text-dark"
                        style={{ fontSize: "0.65rem" }}
                        title="Dados incompletos"
                      >!</span>
                    )}
                    <span className="fw-semibold">{item.description}</span>
                  </div>
                  {payable?.supplier?.entity?.name && (
                    <div className="text-muted small">{payable.supplier.entity.name}</div>
                  )}
                </div>
              );
            },
          },
          {
            label: "Valor",
            render: (item) => <span className="fw-semibold">{formatMoneyBR(item.amount)}</span>,
          },
          { label: "Vencimento", render: (item) => formatDateBR(item.dueDate) },
          {
            label: "Natureza",
            render: (item) => {
              const n = item.operationNature ?? "";
              const label = (OPERATION_NATURE_PT[n] ?? n) || "-";
              const colors: Record<string, string> = {
                OPEX: "text-bg-primary",
                CAPEX: "text-bg-warning text-dark",
                FINANCE: "text-bg-info",
                OTHER: "text-bg-secondary",
              };
              return (
                <span className={`badge ${colors[n] ?? "text-bg-secondary"}`} style={{ fontSize: "0.7rem" }}>
                  {label}
                </span>
              );
            },
          },
          {
            label: "Status",
            render: (item) => {
              const label = PAYABLE_STATUS_PT[item.status] ?? item.status ?? "-";
              const colors: Record<string, string> = {
                OPEN: "text-bg-warning text-dark",
                PAID: "text-bg-success",
                PARTIAL: "text-bg-info",
                CANCELED: "text-bg-secondary",
                OVERDUE: "text-bg-danger",
              };
              return (
                <span className={`badge ${colors[item.status] ?? "text-bg-secondary"}`} style={{ fontSize: "0.7rem" }}>
                  {label}
                </span>
              );
            },
          },
        ]}
        customActions={(item, refresh) => (
          <>
            {item.status !== "PAID" && item.status !== "CANCELED" && (
              <button
                className="btn btn-sm btn-success me-2"
                onClick={() => {
                  setRefreshList(() => refresh);
                  setPaymentModal({
                    show: true,
                    itemId: item.id,
                    itemDescription: item.description,
                    itemAmount: Number(item.amount),
                    itemDueDate: item.dueDate,
                  });
                }}
              >
                Pagar
              </button>
            )}
            {item.status === "PAID" && (
              <button
                className="btn btn-sm btn-outline-warning me-2"
                onClick={() => {
                  setRefreshList(() => refresh);
                  setReverseModal({
                    show: true,
                    itemId: item.id,
                    itemDescription: item.description,
                  });
                }}
              >
                Estornar
              </button>
            )}
          </>
        )}
        formFields={[
          {
            name: "supplierId",
            label: "Fornecedor",
            type: "autocomplete",
            placeholder: "Buscar fornecedor",
            fetchOptions: fetchSuppliers,
            getIdFromItem: (item) => item.supplierId ?? null,
            getLabelFromItem: (item) => item.supplier?.entity?.name || "",
            required: true,
          },
          {
            name: "categoryId",
            label: "Categoria",
            type: "select",
            fetchOptions: fetchCategoryOptions,
            required: true,
          },
          {
            name: "costCenterId",
            label: "Centro de custo",
            type: "select",
            fetchOptions: fetchCostCenterOptions,
            required: true,
          },
          {
            name: "bankAccountId",
            label: "Conta bancária",
            type: "select",
            fetchOptions: fetchBankAccountOptions,
            required: true,
          },
          {
            name: "accountId",
            label: "Conta contábil",
            type: "autocomplete",
            placeholder: "Buscar conta contábil",
            fetchOptions: fetchAccounts,
            getIdFromItem: (item) => item.accountId ?? null,
            getLabelFromItem: (item) =>
              item.account ? `${item.account.code} - ${item.account.name}` : "",
            required: true,
          },
          { name: "description", label: "Descrição", type: "text", required: true },
          {
            name: "amount",
            label: "Valor",
            type: "text",
            required: true,
            displayValue: (value) => {
              const parsed = parseMoneyBR(value);
              return formatMoneyBR(parsed ?? 0);
            },
          },
          {
            name: "dueDate",
            label: "Vencimento",
            type: "date",
            required: true,
            displayValue: (value) => formatDateBR(String(value ?? "")),
          },
        ]}
        initialValues={{
          supplierId: null,
          categoryId: null,
          costCenterId: null,
          bankAccountId: null,
          accountId: null,
          description: "",
          amount: "",
          dueDate: "",
        }}
        mapToPayload={(values) => {
          const amount = parseMoneyBR(values.amount);
          if (amount === null) {
            throw new Error("Valor invalido.");
          }
          return {
            supplierId: values.supplierId ? Number(values.supplierId) : null,
            categoryId: values.categoryId ? Number(values.categoryId) : null,
            costCenterId: values.costCenterId ? Number(values.costCenterId) : null,
            bankAccountId: values.bankAccountId ? Number(values.bankAccountId) : null,
            accountId: values.accountId ? Number(values.accountId) : null,
            description: values.description,
            amount,
            dueDate: values.dueDate,
          };
        }}
        renderCreateHint={
          <div className="alert alert-info mb-3 py-2 small">
            Campos obrigatórios: Fornecedor, Categoria, Centro de custo, Conta bancária e Conta contábil.
            Valor aceita formato 123,45. Vencimento deve ser uma data válida.
          </div>
        }
        renderFormExtras={({ values, setValues, setAutocompleteLabel, editingId }) => (
          <PayableFormExtras
            values={values}
            setValues={setValues}
            setAutocompleteLabel={setAutocompleteLabel}
            editingId={editingId}
          />
        )}
        renderViewContent={({ item }) => {
          const payable = item as PayableDetails | null;
          const payment = payable?.payments?.[0] ?? null;
          const fiscal = payment?.fiscalDocument ?? null;
          const reverse = payable?.movements?.[0] ?? null;
          const reversed = Boolean(reverse?.occurredAt);
          const paymentMethod =
            payment?.paymentMethod?.name ??
            payable?.paymentMethod?.name ??
            "-";
          const paymentDateRaw = payable?.paymentDate ?? payment?.paymentDate ?? null;
          const paymentDateDisplay =
            payable?.status === "PAID" && !paymentDateRaw
              ? "Nao informado"
              : formatDateValue(paymentDateRaw);
          return (
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <div className="border rounded p-3 h-100">
                  <div className="fw-semibold mb-2">Conta</div>
                  <div className="row g-2">
                    <div className="col-6">
                      <div className="text-muted small">Fornecedor</div>
                      <div className="fw-semibold">
                        {payable?.supplier?.entity?.name ?? "-"}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted small">Centro de custo</div>
                      <div className="fw-semibold">
                        {payable?.costCenter
                          ? `${payable.costCenter.code ?? ""} ${payable.costCenter.name}`.trim()
                          : "-"}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted small">Conta contábil</div>
                      <div className="fw-semibold">
                        {payable?.account
                          ? `${payable.account.code} - ${payable.account.name}`
                          : "-"}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted small">Categoria</div>
                      <div className="fw-semibold">{payable?.category?.name ?? "-"}</div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted small">Conta bancária</div>
                      <div className="fw-semibold">
                        {payable?.bankAccount
                          ? `${payable.bankAccount.bank?.name ?? ""} ${payable.bankAccount.agency}/${payable.bankAccount.accountNumber}`.trim()
                          : "-"}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted small">Valor</div>
                      <div className="fw-semibold">{formatMoney(payable?.amount)}</div>
                    </div>
                    <div className="col-12">
                      <div className="text-muted small">Descrição</div>
                      <div className="fw-semibold">{payable?.description ?? "-"}</div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted small">Vencimento</div>
                      <div className="fw-semibold">{formatDateValue(payable?.dueDate)}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="border rounded p-3 h-100">
                  <div className="fw-semibold mb-2">Pagamento</div>
                  <div className="row g-2">
                    <div className="col-6">
                      <div className="text-muted small">Status</div>
                      <div className="fw-semibold">{statusLabel(payable?.status, reversed)}</div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted small">Data de pagamento</div>
                      <div className="fw-semibold">{paymentDateDisplay}</div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted small">Forma de pagamento</div>
                      <div className="fw-semibold">{paymentMethod}</div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted small">Valor pago</div>
                      <div className="fw-semibold">{formatMoney(payable?.paidAmount)}</div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted small">Desconto</div>
                      <div className="fw-semibold">{formatMoney(payable?.discountAmount)}</div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted small">Juros</div>
                      <div className="fw-semibold">{formatMoney(payable?.interestAmount)}</div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted small">Multa</div>
                      <div className="fw-semibold">{formatMoney(payable?.fineAmount)}</div>
                    </div>
                    {reversed && (
                      <>
                        <div className="col-6">
                          <div className="text-muted small">Data do estorno</div>
                          <div className="fw-semibold">{formatDateValue(reverse?.occurredAt)}</div>
                        </div>
                        <div className="col-12">
                          <div className="text-muted small">Motivo do estorno</div>
                          <div className="fw-semibold">{reverse?.description ?? "-"}</div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
              {fiscal && (
                <div className="col-12 col-md-6">
                  <div className="border rounded p-3 h-100">
                    <div className="fw-semibold mb-2">Nota</div>
                    <div className="row g-2">
                      <div className="col-6">
                        <div className="text-muted small">Tipo</div>
                        <div className="fw-semibold">
                          {fiscal.docType === "NFE" ? "NFe" : fiscal.docType === "NFSE" ? "NFSe" : fiscal.docType}
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="text-muted small">Número</div>
                        <div className="fw-semibold">{fiscal.docNumber ?? "-"}</div>
                      </div>
                      <div className="col-12">
                        <div className="text-muted small">Chave</div>
                        <div className="fw-semibold">{fiscal.docKey ?? "-"}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="col-12 col-md-6">
                <div className="border rounded p-3 h-100">
                  <div className="fw-semibold mb-2">Observações</div>
                  <div className="row g-2">
                    <div className="col-12">
                      <div className="text-muted small">Da conta</div>
                      <div className="fw-semibold">{payable?.description ?? "-"}</div>
                    </div>
                    <div className="col-12">
                      <div className="text-muted small">Do pagamento</div>
                      <div className="fw-semibold">{payable?.paymentDescription ?? payment?.description ?? "-"}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        }}
        permissionWrite="PAYABLE_WRITE"
      />
      <PaymentModal
        show={paymentModal.show}
        onClose={() => setPaymentModal({ ...paymentModal, show: false })}
        onSuccess={() => {
          setPaymentModal({ ...paymentModal, show: false });
          refreshList?.();
        }}
        itemId={paymentModal.itemId}
        itemType="payable"
        itemDescription={paymentModal.itemDescription}
        itemAmount={paymentModal.itemAmount}
        itemDueDate={paymentModal.itemDueDate}
      />
      <ReversePaymentModal
        show={reverseModal.show}
        onClose={() => setReverseModal({ ...reverseModal, show: false })}
        onSuccess={() => {
          setReverseModal({ ...reverseModal, show: false });
          refreshList?.();
        }}
        itemId={reverseModal.itemId}
        itemDescription={reverseModal.itemDescription}
        itemType="payable"
      />
    </>
  );
};
